import { makeStyles, Tooltip } from '@fluentui/react-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useIsFeatureEnabled } from '../../../api/features/features';
import { useUserSettings } from '../../../contexts/useUserSettings';
import { useFunctionalAccess } from '../../../hooks/useFunctionalAccess';
import { Report } from '../../../model';
import { cometDarkTheme, customPalette } from '../../../theme';
import { getDateStringAsLocaleString } from '../../../utils/DateUtils';
import { VIEW_REPORT_EXECUTION_SUMMARY_URL, VIEW_REPORT_URL } from '../../../utils/Urls.constant';
import { DefaultTooltipHostV9 } from '../../common/DefaultTooltipHostV9';

interface LastExecutedReportLinkProps extends Report {
    disabled?: boolean;
}

const useStyles = makeStyles({
    executedLink: {
        textDecoration: 'underline',
        color: customPalette.blue075A92,
        ':focus': {
            boxShadow: 'none !important',
            outline: 'none !important',
            textDecorationStyle: 'double',
        },
    },
    disabledItem: {
        color: cometDarkTheme.colorNeutralStrokeDisabled,
    },
});

const LastExecutedReportLink: React.FC<LastExecutedReportLinkProps> = ({ disabled = false, ...report }) => {
    const { t } = useTranslation();
    const userSettingsContext = useUserSettings();
    const navigate = useNavigate();
    const { functionalAccess } = useFunctionalAccess();
    const styles = useStyles();

    const hasViewTaxReturnsEnabled = functionalAccess?.viewTaxReturnsEnabled;

    const { data: isExecutionSummaryEnabled } = useIsFeatureEnabled({
        featureFlagName: 'ExecutionSummaryEnabled',
    });

    const navigateToForm = () => {
        userSettingsContext.updateReturnTabs(
            {
                returnKey: report.id ?? 0,
                returnName: report.name ?? '',
            },
            true
        );
        navigate(VIEW_REPORT_URL + report.id + VIEW_REPORT_EXECUTION_SUMMARY_URL);
    };

    let cellContent = '';
    const executed = report.executed;
    if (executed) {
        cellContent = getDateStringAsLocaleString(executed, true);
    }

    if (cellContent === '' && executed) {
        cellContent = executed;
    }

    const isCanceled = report.executed?.toLowerCase() === 'canceled';
    const anchorAriaLabel = isCanceled
        ? t('lastExecutedAriaLabelCanceled', { calcFileName: report.name }).toString()
        : t('lastExecutedAriaLabel', { calcFileName: report.name, lastExecutedDate: cellContent }).toString();
    const openExecutionSummaryTooltip = t('openExecutionSummaryReport').toString();

    if (isExecutionSummaryEnabled && hasViewTaxReturnsEnabled && !disabled) {
        return (
            <Tooltip content={openExecutionSummaryTooltip} relationship='description' appearance='inverted' withArrow positioning='below'>
                <Link
                    role='link'
                    to={VIEW_REPORT_URL + report.id + VIEW_REPORT_EXECUTION_SUMMARY_URL}
                    tabIndex={-1}
                    aria-label={anchorAriaLabel}
                    className={styles.executedLink}
                    onClick={navigateToForm}
                    data-analytics-button-name='execution_summary_link'
                >
                    {cellContent}
                </Link>
            </Tooltip>
        );
    }

    const isDisabledText = !(report.canExecute && hasViewTaxReturnsEnabled);
    return (
        <DefaultTooltipHostV9
            appearance='inverted'
            withArrow
            positioning='after'
            content={cellContent}
            containerClassName={isDisabledText ? styles.disabledItem : undefined}
        >
            <label
                tabIndex={-1}
                className={isDisabledText ? styles.disabledItem : undefined}
                aria-disabled={!hasViewTaxReturnsEnabled || disabled}
            >
                {cellContent}
            </label>
        </DefaultTooltipHostV9>
    );
};

export default LastExecutedReportLink;
